/** Dashboard state store. */

import { create } from 'zustand';
import type { HypeScore } from '@/types/models';
import type { DashboardResponse, Mention } from '@/types/responses';
import type { MomentumAlert } from '@/utils/momentum';

export type HypeScoreWindow = '24h' | '7d' | '30d';

interface DashboardState {
  data: DashboardResponse | null;
  allMentions: Mention[];
  isLoading: boolean;
  lastUpdate: Date | null;
  hypeScoreWindow: HypeScoreWindow;
  momentumAlerts: Map<string, MomentumAlert>;
  setData: (data: DashboardResponse) => void;
  setAllMentions: (mentions: Mention[]) => void;
  setLoading: (loading: boolean) => void;
  updateLastUpdate: () => void;
  setHypeScoreWindow: (window: HypeScoreWindow) => void;
  patchHypeScores: (hype_score: HypeScore[]) => void;
  mergeMomentumAlerts: (alerts: MomentumAlert[]) => void;
}

export const useDashboardStore = create<DashboardState>((set) => ({
  data: null,
  allMentions: [],
  isLoading: false,
  lastUpdate: null,
  hypeScoreWindow: '24h',
  momentumAlerts: new Map<string, MomentumAlert>(),
  setData: (data: DashboardResponse) => set({ data, lastUpdate: new Date() }),
  setAllMentions: (mentions: Mention[]) => set({ allMentions: mentions }),
  setLoading: (loading: boolean) => set({ isLoading: loading }),
  updateLastUpdate: () => set({ lastUpdate: new Date() }),
  setHypeScoreWindow: (hypeScoreWindow: HypeScoreWindow) => set({ hypeScoreWindow }),
  patchHypeScores: (hype_score: HypeScore[]) =>
    set((s) =>
      s.data
        ? { data: { ...s.data, hype_score }, lastUpdate: new Date() }
        : { lastUpdate: new Date() }
    ),
  mergeMomentumAlerts: (alerts: MomentumAlert[]) =>
    set((s) => {
      if (!alerts.length) {
        return {};
      }
      const next = new Map(s.momentumAlerts);
      alerts.forEach((alert) => {
        const key = alert.ticker.toUpperCase();
        const prev = next.get(key);
        if (!prev || alert.timestamp >= prev.timestamp) {
          next.set(key, { ...alert, ticker: key });
        }
      });
      return { momentumAlerts: next, lastUpdate: new Date() };
    }),
}));

